import {
  AfterViewChecked,
  Component,
  DoCheck,
  ElementRef,
  HostBinding,
  Input,
  OnDestroy,
  Optional,
  Self,
  ViewChild,
  ChangeDetectionStrategy,
  signal,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  NgControl,
  FormsModule,
} from '@angular/forms';
import { COMMA, ENTER, SEMICOLON, TAB } from '@angular/cdk/keycodes';
import { FocusMonitor, FocusOrigin } from '@angular/cdk/a11y';
import { MatFormFieldControl } from '@angular/material/form-field';
import { MatChipInputEvent, MatChipsModule } from '@angular/material/chips';
import { Subject } from 'rxjs';
import { EMAIL_REGEXP } from '@core/actions/util';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'app-chips-emails-input',
  templateUrl: './chips-emails-input.component.html',
  styleUrls: ['./chips-emails-input.component.scss'],
  providers: [
    {
      provide: MatFormFieldControl,
      useExisting: ChipsEmailsInputComponent,
    },
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [MatChipsModule, MatIconModule, FormsModule],
})
export class ChipsEmailsInputComponent
  implements
    ControlValueAccessor,
    MatFormFieldControl<string[]>,
    DoCheck,
    OnDestroy
{
  private static nextId = 0;
  private _placeholder: string = null;
  private _required = false;
  private _disabled = false;
  /**
   * A signal, not a plain field: the chip list is rendered straight from it, so
   * every writer — the `@Input`, `writeValue`, and `remove()` — has to schedule
   * its own refresh. A plain field only rendered when the caller happened to be
   * a template listener, which is what the manual `detectChanges()` in
   * `remove()` used to paper over.
   */
  private readonly _value = signal<string[] | null>(null);
  focused = false;
  errorState = false;
  stateChanges: Subject<void> = new Subject<void>();
  emailInputValue = '';
  @Input() separatorKeysCodes: number[] = [ENTER, COMMA, SEMICOLON, TAB];
  @Input() separatorChars = /[;,\n\r\t]/;

  get empty(): boolean {
    return Array.isArray(this.value) ? this.value.length === 0 : !this.value;
  }

  @Input()
  get value(): string[] {
    return this._value();
  }

  set value(value: string[]) {
    this._value.set(value);
    this.handleOnChange(value);
    this.stateChanges.next();
  }

  get validValues(): { value: string; valid: boolean }[] {
    return this._value().map((value) => ({
      value,
      valid: EMAIL_REGEXP.test(value),
    }));
  }

  validTest(value: string) {
    return EMAIL_REGEXP.test(value);
  }

  @Input()
  get required(): boolean {
    return this._required;
  }

  set required(required: boolean) {
    this._required = !!required;
    this.stateChanges.next();
  }

  @Input()
  get disabled(): boolean {
    return this._disabled;
  }

  set disabled(value: boolean) {
    this._disabled = !!value;
    this.stateChanges.next();
  }

  @Input()
  get placeholder(): string {
    return this._placeholder;
  }

  set placeholder(placeholder: string) {
    this._placeholder = placeholder;
    this.stateChanges.next();
  }

  get emailsInputControl(): FormControl<string[]> | null {
    return <FormControl<string[]> | null>this.ngControl?.control ?? null;
  }

  @Input() maxValueLength = 15;
  @Input({
    transform: coerceBooleanProperty,
  })
  transformLowercase = false;
  @HostBinding()
  id = `app-chiplist-input-${ChipsEmailsInputComponent.nextId++}`;
  @HostBinding('attr.aria-describedby') describedBy = '';

  @HostBinding('class.floating') get shouldLabelFloat(): boolean {
    return this.focused || !this.empty || !!this.emailInputValue;
  }

  @ViewChild('emailInputControl') emailTempInputControl: NgControl;
  @ViewChild('inputElement')
  tempEmailInputElement: ElementRef<HTMLInputElement>;

  constructor(
    @Optional() @Self() public ngControl: NgControl,
    private focusMonitor: FocusMonitor,
    private elementRef: ElementRef<HTMLElement>
  ) {
    if (this.ngControl !== null) {
      this.ngControl.valueAccessor = this;
    }

    this.focusMonitor
      .monitor(this.elementRef.nativeElement, true)
      .subscribe((origin: FocusOrigin) => {
        this.focused = !!origin;
        this.stateChanges.next();
      });
  }

  ngOnDestroy(): void {
    this.stateChanges.complete();
    this.focusMonitor.stopMonitoring(this.elementRef.nativeElement);
  }

  handleOnChange: (v: any) => void = (v: any) => {
    return;
  };
  handleOnTouched: () => void = () => {
    return;
  };

  writeValue(value: any): void {
    this.value = Array.isArray(value) ? value : null;
  }

  registerOnChange(fn: (v: any) => void): void {
    this.handleOnChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.handleOnTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onContainerClick(event: MouseEvent): void {
    if ((event.target as Element).tagName.toLowerCase() !== 'input') {
      const matChipInput: HTMLElement =
        this.elementRef.nativeElement.querySelector('input');

      if (matChipInput) {
        matChipInput.focus();
      }
    }
  }

  setDescribedByIds(ids: string[]): void {
    this.describedBy = ids.join(' ');
  }

  ngDoCheck(): void {
    if (this.ngControl) {
      this.errorState = this.ngControl.invalid && this.ngControl.touched;
      this.stateChanges.next();
    }
  }

  add(event: MatChipInputEvent) {
    const value: string = event.value ? event.value.trim() : event.value;
    this._add(value);
  }

  private _add(value: string): void {
    if (this.emailsInputControl) {
      this.emailsInputControl.setErrors(null);
      this.emailsInputControl.updateValueAndValidity();
    }
    if (this.emailTempInputControl.valid) {
      if (value) {
        const fieldValue: string[] = this.value ? [...this.value] : [];
        fieldValue.push(this.transformLowercase ? value.toLowerCase() : value);

        this.value = fieldValue;
      }
      const input = this.tempEmailInputElement.nativeElement;
      if (input) {
        this.emailInputValue = '';
      }
    } else if (this.emailsInputControl) {
      this.emailsInputControl.setErrors({ email: true });
      this.emailsInputControl.markAsTouched();
    }
  }

  /**
   * Removing the trailing `if (this.value.length < 1) detectChanges()` is safe,
   * and the asymmetry it carried is the tell: removing a *non-last* chip leaves
   * Material a next chip to focus, and that focus event produced the zone tick
   * that rendered the removal; removing the last one produces no follow-on
   * event, so nothing ticked and the chip stayed on screen. It was a missing
   * tick, not a missing render pass. The `(removed)` listener already marks
   * this view dirty and notifies the change-detection scheduler, and the
   * `value` setter below emits `stateChanges` so the surrounding
   * `MatFormFieldControl` sees `empty` flip — which the local `detectChanges()`
   * never told it.
   */
  remove(val: string): void {
    const value: string[] = this.value ? [...this.value] : [];
    const index: number = value.indexOf(val);

    if (index >= 0) {
      value.splice(index, 1);
      this.value = value;
    }
  }

  paste(event: ClipboardEvent) {
    event.preventDefault(); //Prevents the default action
    event.clipboardData
      .getData('Text') //Gets the text pasted
      .split(this.separatorChars) //Splits it when a SEMICOLON or COMMA or NEWLINE
      .forEach((value) => {
        const trimmedValue = this.transformLowercase
          ? value.trim().toLowerCase()
          : value.trim();
        if (trimmedValue) {
          this.emailInputValue = trimmedValue;
          this._add(trimmedValue);
        }
      });
  }
}
